import React, { useState, useRef, useEffect } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import GooglePlacesAutocomplete from "react-google-places-autocomplete";
import Hero from "../../components/Hero/Hero";
import PageFooter from "../../components/PageFooter/PageFooter";
import Modal from "../../components/ui/Modal";
import FormMoreInfo from "../../components/FormMoreInfo/FormMoreInfo";
import toast from "../../components/ui/toast";
import "./reviewSemanticAnalysis.css";
import Seo from "../../i18n/Seo";
import useLang from "../../i18n/useLang";
import { demoUrl } from "../../i18n/links";
import content from "./content";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4000/tech";

function safeText(val) {
  if (val == null) return "";
  if (typeof val === "string") return val;
  if (typeof val === "number") return String(val);
  if (typeof val === "object") {
    const parts = [
      ...Object.keys(val).filter((k) => typeof k === "string" && k.length > 5),
      ...Object.values(val).filter((v) => typeof v === "string"),
    ];
    if (parts.length > 0) return parts.join(" ");
    return JSON.stringify(val);
  }
  return String(val);
}

const MAX_REVIEWS = 500;
const MAX_THEMES = 5;

const AVAILABLE_THEMES = [
  { id: "accueil", icon: "👋" },
  { id: "service_client", icon: "🤝" },
  { id: "expertise", icon: "🎯" },
  { id: "reactivite", icon: "⚡" },
  { id: "qualite_produits", icon: "✨" },
  { id: "variete_choix", icon: "🛒" },
  { id: "rapport_qualite_prix", icon: "💰" },
  { id: "proprete", icon: "🧹" },
  { id: "ambiance", icon: "🏪" },
  { id: "accessibilite", icon: "🅿️" },
  { id: "confort", icon: "🛋️" },
  { id: "temps_attente", icon: "⏱️" },
  { id: "horaires", icon: "🕐" },
  { id: "livraison", icon: "📦" },
  { id: "disponibilite", icon: "📋" },
  { id: "communication", icon: "💬" },
  { id: "sav", icon: "🔧" },
  { id: "fidelisation", icon: "❤️" },
  { id: "recommandation", icon: "👍" },
  { id: "reservation", icon: "📱" },
  { id: "transparence", icon: "🔍" },
  { id: "fiabilite", icon: "🛡️" },
  { id: "securite", icon: "🔒" },
  { id: "site_web", icon: "💻" },
];

const SENTIMENT_COLORS = {
  positive: "#10b981",
  negative: "#ef4444",
  neutral: "#6b7280",
  mixed: "#f59e0b",
};

const ScoreBar = ({ score }) => {
  if (score === null || score === undefined) return null;
  const color = score >= 70 ? "#10b981" : score >= 40 ? "#f59e0b" : "#ef4444";
  return (
    <div className="rsa-score-bar">
      <div
        className="rsa-score-bar__fill"
        style={{ width: `${score}%`, background: color }}
      />
    </div>
  );
};

const SentimentDot = ({ sentiment, labels }) => {
  const color = SENTIMENT_COLORS[sentiment] || "#6b7280";
  return (
    <span
      className="rsa-sentiment-dot"
      style={{ background: color }}
      title={labels[sentiment] || sentiment}
    />
  );
};

function parseCSV(text) {
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
  if (lines.length < 2) return [];
  const header = lines[0].toLowerCase().replace(/\s+/g, "");
  if (!header.includes("review_content") && !header.includes("reviewcontent"))
    return [];
  const results = [];
  for (let i = 1; i < lines.length; i++) {
    const cols = parseCSVLine(lines[i]);
    const content = (cols[0] || "").trim();
    if (!content) continue;
    const rating = cols[1] ? parseFloat(cols[1]) : null;
    const date = cols[2] ? cols[2].trim() : null;
    results.push({
      review_content: content,
      rating: rating && rating >= 1 && rating <= 5 ? rating : null,
      review_date: date || null,
    });
  }
  return results;
}

function parseCSVLine(line) {
  const cols = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"' && line[i + 1] === '"') {
        current += '"';
        i++;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        current += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === "," || ch === ";") {
      cols.push(current);
      current = "";
    } else {
      current += ch;
    }
  }
  cols.push(current);
  return cols;
}

const ReviewSemanticAnalysis = () => {
  const lang = useLang();
  const t = content[lang];
  const [businessName, setBusinessName] = useState("");
  const [businessAddress, setBusinessAddress] = useState("");
  const [reviewsText, setReviewsText] = useState("");
  const [reviews, setReviews] = useState([]);
  const [selectedThemes, setSelectedThemes] = useState([
    "accueil",
    "service_client",
    "qualite_produits",
    "rapport_qualite_prix",
    "temps_attente",
  ]);
  const [inputMode, setInputMode] = useState("text");
  const [csvFileName, setCsvFileName] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [isSA, setIsSA] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const formRef = useRef(null);
  const fileInputRef = useRef(null);
  const recaptchaRef = useRef();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("access") === "localranker") setIsSA(true);
  }, []);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const parseTextReviews = (text) =>
    text
      .split(/\n{2,}/)
      .map((r) => r.trim())
      .filter((r) => r.length > 0)
      .map((r) => ({ review_content: r, rating: null, review_date: null }));

  useEffect(() => {
    if (inputMode === "text") setReviews(parseTextReviews(reviewsText));
  }, [reviewsText, inputMode]);

  const toggleTheme = (id) => {
    setSelectedThemes((prev) => {
      if (prev.includes(id)) return prev.filter((themeId) => themeId !== id);
      if (prev.length >= MAX_THEMES) {
        toast.warning(t.toasts.maxThemes(MAX_THEMES));
        return prev;
      }
      return [...prev, id];
    });
  };

  const handleCSVFile = (file) => {
    if (!file) return;
    if (!file.name.match(/\.(csv|tsv|txt)$/i)) {
      toast.warning(t.toasts.badFormat);
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const parsed = parseCSV(e.target.result);
      if (parsed.length === 0) {
        toast.warning(t.toasts.noReviewsInCsv);
        return;
      }
      setReviews(parsed);
      setCsvFileName(file.name);
      setInputMode("csv");
    };
    reader.readAsText(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleCSVFile(e.dataTransfer?.files?.[0]);
  };

  const clearCSV = () => {
    setCsvFileName(null);
    setInputMode("text");
    setReviews(parseTextReviews(reviewsText));
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const downloadTemplate = () => {
    const blob = new Blob([t.csvTemplate], { type: "text/csv;charset=utf-8;" });
    const u = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = u;
    a.download = "reviews_template.csv";
    a.click();
    URL.revokeObjectURL(u);
  };

  const handlePlaceSelect = ({ description }) => {
    if (!description) return;
    const parts = description.split(",");
    setBusinessName(parts[0]?.trim() || description);
    setBusinessAddress(description);
  };

  const handleSubmit = () => {
    if (reviews.length === 0) {
      toast.warning(t.toasts.noReviews);
      return;
    }
    if (selectedThemes.length === 0) {
      toast.warning(t.toasts.noThemes);
      return;
    }

    if (isSA) {
      doSubmit();
    } else {
      setModalVisible(true);
    }
  };

  const doSubmit = async (leadInfo) => {
    setModalVisible(false);

    const tokenCaptcha = isSA
      ? true
      : await recaptchaRef.current?.executeAsync();
    if (!tokenCaptcha) {
      toast.warning(t.toasts.captcha);
      return;
    }

    setIsLoading(true);
    setResult(null);

    try {
      const params = new URLSearchParams();
      if (isSA) params.append("sa", "true");
      params.append("lang", lang);
      const qs = params.toString();

      const themes = selectedThemes.map((id) => ({
        id,
        name: t.themes[id] || id,
      }));

      const payload = reviews.slice(0, MAX_REVIEWS).map((r) => ({
        review_content: r.review_content,
        ...(r.rating && { rating: r.rating }),
        ...(r.review_date && { review_date: r.review_date }),
      }));

      const response = await fetch(
        `${API_URL}/reviews-semantic-analysis${qs ? `?${qs}` : ""}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            reviews: payload,
            themes,
            businessName: businessName || undefined,
            businessAddress: businessAddress || undefined,
            ...(leadInfo && {
              businessInfo: {
                ...leadInfo,
                businessName: businessName || undefined,
              },
            }),
          }),
        },
      );

      const data = await response.json();
      if (!response.ok) {
        toast.error(data.error || t.toasts.error);
        return;
      }
      setResult({
        ...data,
        businessName: businessName || null,
        businessAddress: businessAddress || null,
      });
    } catch (e) {
      console.error(e);
      toast.error(t.toasts.network);
    } finally {
      setIsLoading(false);
      recaptchaRef.current?.reset();
    }
  };

  const reviewCount = reviews.length;

  return (
    <div className="rsa-page">
      <Seo
        routeId="reviewSentimentAnalysis"
        title={t.seo.title}
        description={t.seo.description}
      />

      <Hero
        title={t.hero.title}
        subtitle={t.hero.subtitle}
        ctaText={t.hero.cta}
        ctaOnClick={scrollToForm}
      />

      <div className="rsa-form" ref={formRef}>
        <div className="rsa-form__card">
          {/* Step 1: Business */}
          <div className="rsa-form__step">
            <div className="rsa-form__step-label">
              <span className="rsa-form__step-num">1</span>
              {t.form.step1}
            </div>
            <div className="rsa-form__place-input">
              <GooglePlacesAutocomplete
                placeholder={t.form.placePlaceholder}
                autocompletionRequest={
                  lang === "en" ? { language: "en" } : undefined
                }
                onSelect={handlePlaceSelect}
                renderSuggestions={(active, suggestions, onSelectSuggestion) => (
                  <div className="rsa-suggestions">
                    {suggestions.map((suggestion, index) => (
                      <div
                        className="rsa-suggestion"
                        key={index}
                        onClick={(event) =>
                          onSelectSuggestion(suggestion, event)
                        }
                      >
                        <svg
                          className="rsa-suggestion__icon"
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                          <circle cx="12" cy="10" r="3" />
                        </svg>
                        {suggestion.description}
                      </div>
                    ))}
                  </div>
                )}
              />
            </div>
            {businessAddress && (
              <p className="rsa-form__place-selected">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                {businessAddress}
              </p>
            )}
          </div>

          <div className="rsa-form__divider" />

          {/* Step 2: Input */}
          <div className="rsa-form__step">
            <div className="rsa-form__step-label">
              <span className="rsa-form__step-num">2</span>
              {t.form.step2}
            </div>
            <div className="rsa-form__tabs">
              <button
                type="button"
                className={`rsa-form__tab${inputMode === "text" ? " rsa-form__tab--active" : ""}`}
                onClick={clearCSV}
              >
                {t.form.tabPaste}
              </button>
              <button
                type="button"
                className={`rsa-form__tab${inputMode === "csv" ? " rsa-form__tab--active" : ""}`}
                onClick={() => fileInputRef.current?.click()}
              >
                {t.form.tabCsv}
              </button>
            </div>

            {inputMode === "text" && (
              <>
                <div
                  className={`rsa-form__drop-zone${isDragging ? " rsa-form__drop-zone--active" : ""}`}
                  onDrop={handleDrop}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setIsDragging(true);
                  }}
                  onDragLeave={() => setIsDragging(false)}
                >
                  <textarea
                    className="rsa-form__textarea"
                    placeholder={t.form.textareaPlaceholder}
                    value={reviewsText}
                    onChange={(e) => setReviewsText(e.target.value)}
                    rows={5}
                  />
                  {isDragging && (
                    <div className="rsa-form__drop-overlay">
                      <span>{t.form.dropHere}</span>
                    </div>
                  )}
                </div>
                <p className="rsa-form__hint">
                  {t.form.dropHint}{" "}
                  <button
                    type="button"
                    className="rsa-form__link"
                    onClick={downloadTemplate}
                  >
                    {t.form.downloadTemplate}
                  </button>
                </p>
              </>
            )}

            {inputMode === "csv" && (
              <div className="rsa-form__csv-file">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                <span className="rsa-form__csv-name">{csvFileName}</span>
                <span className="rsa-form__csv-count">
                  {t.form.reviewCount(reviewCount)}
                </span>
                <button
                  type="button"
                  className="rsa-form__csv-remove"
                  onClick={clearCSV}
                >
                  &times;
                </button>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.tsv,.txt"
              style={{ display: "none" }}
              onChange={(e) => handleCSVFile(e.target.files?.[0])}
            />
          </div>

          <div className="rsa-form__divider" />

          {/* Step 3: Theme picker */}
          <div className="rsa-form__step">
            <div className="rsa-form__step-label">
              <span className="rsa-form__step-num">3</span>
              {t.form.step3} ({selectedThemes.length}/{MAX_THEMES})
            </div>
            <div className="rsa-form__theme-grid">
              {AVAILABLE_THEMES.map((theme) => {
                const active = selectedThemes.includes(theme.id);
                return (
                  <button
                    key={theme.id}
                    type="button"
                    className={`rsa-form__theme-chip${active ? " rsa-form__theme-chip--active" : ""}`}
                    onClick={() => toggleTheme(theme.id)}
                  >
                    <span className="rsa-form__theme-chip-icon">{theme.icon}</span>
                    {t.themes[theme.id]}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="rsa-form__divider" />

          {/* Footer */}
          <div className="rsa-form__footer">
            <div className="rsa-form__footer-info">
              <span className="rsa-form__count">
                {t.form.reviewCount(reviewCount)}
                {reviewCount > MAX_REVIEWS && (
                  <span className="rsa-form__count--warn">
                    {" "}(max {MAX_REVIEWS})
                  </span>
                )}
              </span>
            </div>
            <button
              className="rsa-form__btn"
              type="button"
              onClick={handleSubmit}
              disabled={
                isLoading || reviewCount === 0 || selectedThemes.length === 0
              }
            >
              {isLoading ? t.form.submitting : t.form.submit}
            </button>
          </div>
        </div>
      </div>

      <ReCAPTCHA
        sitekey="6LcLC9UZAAAAAOY_vgaU__i3Qhs1tmHtvbibjD2V"
        size="invisible"
        ref={recaptchaRef}
      />

      {isLoading && (
        <div className="rsa-loader">
          <div className="rsa-loader__bar" />
          <p className="rsa-loader__text">
            {t.loading(reviewCount)}
          </p>
        </div>
      )}

      {result && (
        <div className="rsa-results">
          <div className="rsa-results__header">
            <h2>
              {result.businessName
                ? t.results.reportFor(result.businessName)
                : t.results.report}
            </h2>
            <p className="rsa-results__meta">
              {result.businessAddress && (
                <span className="rsa-results__address">
                  {result.businessAddress}
                </span>
              )}
              {t.results.analyzed(result.totalReviews)}
              {result.avgRating !== null && (
                <span>
                  {" "}&middot; {t.results.avgRating}{" "}
                  <strong>{result.avgRating}/5</strong>
                </span>
              )}
            </p>
          </div>

          {/* Global score */}
          <div className="rsa-global">
            <div className="rsa-global__score-block">
              <div
                className="rsa-global__score-ring"
                data-sentiment={result.globalSentiment}
              >
                <span className="rsa-global__score-num">
                  {result.globalScore ?? "—"}
                </span>
                <span className="rsa-global__score-label">/100</span>
              </div>
              <span
                className="rsa-global__sentiment-tag"
                style={{
                  color:
                    SENTIMENT_COLORS[result.globalSentiment] || "#6b7280",
                }}
              >
                {t.sentiments[result.globalSentiment] || "—"}
              </span>
            </div>
            {safeText(result.executiveSummary) && (
              <div className="rsa-global__summary">
                <p>{safeText(result.executiveSummary)}</p>
              </div>
            )}
          </div>

          {/* Themes */}
          {result.themes?.length > 0 && (
            <div className="rsa-section">
              <h3 className="rsa-section__title">{t.results.byTheme}</h3>
              <div className="rsa-theme-list">
                {result.themes.map((th) => (
                  <div key={th.id} className="rsa-theme-row">
                    <div className="rsa-theme-row__head">
                      <SentimentDot sentiment={th.sentiment} labels={t.sentiments} />
                      <span className="rsa-theme-row__name">{th.name}</span>
                      <span className="rsa-theme-row__rate">
                        {safeText(th.mentionRate)}
                      </span>
                      <span className="rsa-theme-row__score">
                        {th.score ?? "—"}/100
                      </span>
                    </div>
                    <ScoreBar score={th.score} />
                    {th.insight && (
                      <p className="rsa-theme-row__insight">
                        {safeText(th.insight)}
                      </p>
                    )}
                    {th.verbatims?.length > 0 && (
                      <div className="rsa-theme-row__verbatims">
                        {th.verbatims.map((v, i) => {
                          const text = safeText(v);
                          return (
                            <span
                              key={i}
                              className={`rsa-verbatim ${text.startsWith("+") ? "rsa-verbatim--pos" : "rsa-verbatim--neg"}`}
                            >
                              {text}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Detected themes */}
          {result.detectedThemes?.length > 0 && (
            <div className="rsa-section">
              <h3 className="rsa-section__title">
                {t.results.detected}
              </h3>
              <div className="rsa-detected-list">
                {result.detectedThemes.map((th, i) => (
                  <div key={i} className="rsa-detected-chip">
                    <SentimentDot sentiment={th.sentiment} labels={t.sentiments} />
                    <span>{safeText(th.name)}</span>
                    <span className="rsa-detected-chip__score">
                      {th.score ?? "—"}/100
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* SWOT */}
          {result.swot && (
            <div className="rsa-section">
              <h3 className="rsa-section__title">{t.results.swot}</h3>
              <div className="rsa-swot">
                <div className="rsa-swot__card rsa-swot__card--s">
                  <div className="rsa-swot__card-header">{t.results.strengths}</div>
                  <ul>
                    {(result.swot.strengths || []).map((s, i) => (
                      <li key={i}>{safeText(s)}</li>
                    ))}
                  </ul>
                </div>
                <div className="rsa-swot__card rsa-swot__card--w">
                  <div className="rsa-swot__card-header">{t.results.weaknesses}</div>
                  <ul>
                    {(result.swot.weaknesses || []).map((s, i) => (
                      <li key={i}>{safeText(s)}</li>
                    ))}
                  </ul>
                </div>
                <div className="rsa-swot__card rsa-swot__card--o">
                  <div className="rsa-swot__card-header">{t.results.opportunities}</div>
                  <ul>
                    {(result.swot.opportunities || []).map((s, i) => (
                      <li key={i}>{safeText(s)}</li>
                    ))}
                  </ul>
                </div>
                <div className="rsa-swot__card rsa-swot__card--t">
                  <div className="rsa-swot__card-header">{t.results.threats}</div>
                  <ul>
                    {(result.swot.threats || []).map((s, i) => (
                      <li key={i}>{safeText(s)}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Recommendations */}
          {result.recommendations?.length > 0 && (
            <div className="rsa-section">
              <h3 className="rsa-section__title">{t.results.recommendations}</h3>
              <div className="rsa-reco-list">
                {result.recommendations.map((r, i) => (
                  <div key={i} className="rsa-reco">
                    <span className="rsa-reco__num">{i + 1}</span>
                    <p>{safeText(r)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Demo CTA */}
          <div className="rsa-demo">
            <span className="rsa-demo__badge">Localranker</span>
            <p className="rsa-demo__title">
              {t.demo.title}
            </p>
            <p className="rsa-demo__desc">
              {t.demo.desc}
            </p>
            <a
              className="rsa-demo__cta"
              href={demoUrl(lang, "reviews-semantic-analysis")}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t.demo.cta}
            </a>
          </div>
        </div>
      )}

      {/* SEO */}
      <section className="rsa-seo">
        <div className="rsa-seo__inner">
          <article className="rsa-seo__block">
            <h2>{t.seoSection.whyTitle}</h2>
            <p>{t.seoSection.whyText}</p>
          </article>

          <article className="rsa-seo__block">
            <h2>{t.seoSection.howTitle}</h2>
            <p>{t.seoSection.howText}</p>
            <div className="rsa-seo__steps">
              {t.seoSection.steps.map((step, i) => (
                <div key={i} className="rsa-seo__step">
                  <span className="rsa-seo__step-num">{i + 1}</span>
                  <div>
                    <strong>{step.title}</strong>
                    <p>{step.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </article>

          <article className="rsa-seo__block">
            <h2>{t.seoSection.reputationTitle}</h2>
            <p>{t.seoSection.reputationText}</p>
          </article>
        </div>
      </section>

      <Modal
        title={t.modal.title}
        visible={modalVisible}
        onClose={() => setModalVisible(false)}
      >
        <FormMoreInfo
          onSubmit={doSubmit}
          submitLabel={t.modal.submit}
        />
      </Modal>

      <PageFooter />
    </div>
  );
};

export default ReviewSemanticAnalysis;
